import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import { Link } from 'react-router-dom';
import { useIsHongKongIp } from '../../../hooks/useIsHongKongIp';

export default function HeroSection() {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const { canShowRestrictedContent } = useIsHongKongIp();
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const isChineseHome = currentLanguage === 'hk' || currentLanguage === 'cn';
  const accountOpeningGuide = currentLanguage === 'vn'
    ? '/pdf/vn/AccountOpening.pdf'
    : '/pdf/AccountOpenning.pdf';
  const depositWithdrawGuide = currentLanguage === 'vn'
    ? '/pdf/vn/DepositWithdraw.pdf'
    : '/pdf/DepositWithdraw.pdf';

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section
      className="site-hero relative flex items-center justify-center bg-cover bg-center bg-no-repeat overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('/img/768bd75e75ad5419eb79b56a0fb32913.jpg')`
      }}
    >
      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-orange-400 rounded-full opacity-30 animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
        <div
          className={`text-center text-white transition-all duration-1000 transform ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          <h1
            className="text-4xl md:text-6xl font-bold mb-6 leading-tight bg-gradient-to-r from-orange-400 via-orange-300 to-orange-500 bg-clip-text text-transparent"
            style={
              isChineseHome
                ? {
                    whiteSpace: 'nowrap',
                    fontSize: 'min(3.75rem, calc((100vw - 3rem) / 11))',
                    letterSpacing: '-0.02em',
                  }
                : undefined
            }
          >
            {t('hero.title')}
          </h1>

          <p
            className={`text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto leading-relaxed transition-all duration-1000 delay-300 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            {t('hero.subtitle')}
          </p>

          {canShowRestrictedContent && (
            <div
              className={`flex flex-col sm:flex-row flex-wrap gap-4 justify-center items-center transition-all duration-1000 delay-500 transform ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
            >
              <Link to="https://portal.jlbhk.com/">
                <Button
                  variant="primary"
                  size="lg"
                  className="bg-gradient-to-r from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white font-semibold px-8 py-4 text-lg transform hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-orange-400/25"
                >
                  <i className="ri-line-chart-line mr-2 animate-pulse"></i>
                  {t('hero.cta.start')}
                </Button>
              </Link>

              <Link to="/platform">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-black px-8 py-4 text-lg transform hover:scale-105 transition-all duration-300 backdrop-blur-sm"
                >
                  <i className="ri-download-2-line mr-2"></i>
                  {t('hero.cta.learn')}
                </Button>
              </Link>

              <a href={accountOpeningGuide} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black px-8 py-4 text-lg transform hover:scale-105 transition-all duration-300"
                >
                  <i className="ri-file-text-line mr-2"></i>
                  {t('hero.cta.accountOpening')}
                </Button>
              </a>

              <a href={depositWithdrawGuide} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black px-8 py-4 text-lg transform hover:scale-105 transition-all duration-300"
                >
                  <i className="ri-exchange-dollar-line mr-2"></i>
                  {t('hero.cta.depositWithdraw')}
                </Button>
              </a>
            </div>
          )}

          {/* Trust Indicators with enhanced effects */}
          <div
            className={`mt-12 flex flex-wrap justify-center items-center gap-8 text-sm text-gray-300 transition-all duration-1000 delay-700 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="flex items-center space-x-2 hover:text-green-400 transition-colors duration-300 cursor-pointer group">
              <i className="ri-shield-check-line text-green-400 group-hover:animate-spin"></i>
              <span>{t('home.licenseHK')}</span>
            </div>

            <div className="flex items-center space-x-2 hover:text-blue-400 transition-colors duration-300 cursor-pointer group">
              <i className="ri-bank-line text-blue-400 group-hover:animate-pulse"></i>
              <span>{t('home.bankSec')}</span>
            </div>

            <div className="flex items-center space-x-2 hover:text-orange-400 transition-colors duration-300 cursor-pointer group">
              <i className="ri-customer-service-2-line text-orange-400 group-hover:animate-bounce"></i>
              <span>{t('home.247s')}</span>
            </div>

            <div className="flex items-center space-x-2 hover:text-purple-400 transition-colors duration-300 cursor-pointer group">
              <i className="ri-global-line text-purple-400 group-hover:animate-spin"></i>
              <span>{t('home.global')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Animated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-orange-400/5 via-transparent to-orange-400/5 animate-pulse"></div>
    </section>
  );
}
